import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { CircleCheck } from 'lucide-react';
import { z } from 'zod';
import { Form } from '@/components/ui/form';
import {
  PCMP_MKT_APPROVE_LABEL,
  PCMP_MKT_COMMENT_MAX,
} from '../../constants/pcmpProposal.constants';
import { useApprovePcmpProposal } from '../../hooks/useApprovePcmpProposal';
import type { PcmpMktApprovalKind } from '../../types/pcmpProposal.types';
import { PcmpEditDialog } from '../PcmpProposalEdit/PcmpEditDialog';
import { PcmpCommentField } from './PcmpCommentField';

const approveFormSchema = z.object({
  comment: z.string().trim().max(PCMP_MKT_COMMENT_MAX),
});

type ApproveFormValues = z.infer<typeof approveFormSchema>;

export interface PcmpMarketingApproveTarget {
  proposalId: number;
  refNo: string;
  kind: PcmpMktApprovalKind;
  levelNo: number | null;
}

interface PcmpMarketingApproveDialogProps {
  target: PcmpMarketingApproveTarget;
  onClose: () => void;
}

const KIND_NOTE: Record<PcmpMktApprovalKind, string> = {
  LEVEL: 'Your approval counts towards Marketing recommending this proposal.',
  ADMIN_FALLBACK:
    'The approval chain is too short, so an admin approval recommends this proposal on its own.',
  ENDORSE:
    'Marketing has already recommended this proposal. Your endorsement is recorded on its timeline.',
};

/** Legacy MktApprove; the comment is optional. */
export function PcmpMarketingApproveDialog({ target, onClose }: PcmpMarketingApproveDialogProps) {
  const form = useForm<ApproveFormValues>({
    resolver: zodResolver(approveFormSchema),
    defaultValues: { comment: '' },
  });
  const { mutate, isPending, error } = useApprovePcmpProposal(
    target.proposalId,
    target.kind,
    onClose,
  );
  const label = PCMP_MKT_APPROVE_LABEL[target.kind](target.levelNo);

  const onSubmit = form.handleSubmit((values) => mutate({ comment: values.comment || undefined }));

  return (
    <Form {...form}>
      <PcmpEditDialog
        open
        onOpenChange={(open) => !open && onClose()}
        title={label}
        description={target.refNo}
        icon={CircleCheck}
        isDirty={form.formState.isDirty}
        isPending={isPending}
        error={error}
        onSubmit={(event) => void onSubmit(event)}
        submitLabel={label}
        pendingLabel="Submitting..."
      >
        <p className="text-sm text-muted-foreground">{KIND_NOTE[target.kind]}</p>
        <PcmpCommentField
          control={form.control}
          name="comment"
          label="Comment"
          placeholder="Optional"
        />
      </PcmpEditDialog>
    </Form>
  );
}
